import { useEffect, useMemo, useState } from "react";
import { useStateValue } from "../context/StateProvider";
import { doc, setDoc } from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";
import {
  getAllusers,
  getListingAppointments,
  saveListingAppointment,
  updateListingAppointment,
  deleteListingAppointment,
  saveListingLead,
  updateListingLead,
} from "../utils/firebasefunctions";
import { actionType } from "../context/reducer";
import { toast } from "react-toastify";
import { formatNumberShort } from "../utils/helper";
import YearFilterDropdown from "../components/team-detail/YearFilterDropdown";
import PageHeader from "../components/PageHeader";
import { subscribeOpenHouses } from "../utils/firebasefunctions";
import {
  Target,
  X,
  Save,
  ChevronRight,
  Plus,
  Calendar,
  MapPin,
  Trash2,
  ClipboardList,
} from "lucide-react";

// Goal data lives in arrays on each user document:
//   yearlyGoals:              [{ year, amount }]   ← GCI (commission $)
//   closedvolumeyearlygoals:  [{ year, amount }]   ← Volume ($)
//   closedyearlygoals:        [{ year, count }]    ← Units (#)
//   openhouseyearlygoals:     [{ year, count }]    ← Open Houses (#)
// We keep that schema to avoid migrating existing data.

function getGoalsFor(user, year) {
  return {
    gci: user?.yearlyGoals?.find((g) => g.year === year)?.amount ?? "",
    volume:
      user?.closedvolumeyearlygoals?.find((g) => g.year === year)?.amount ?? "",
    units:
      user?.closedyearlygoals?.find((g) => g.year === year)?.count ?? "",
    openHouses:
      user?.openhouseyearlygoals?.find((g) => g.year === year)?.count ?? "",
  };
}

function upsertEntry(arr, year, key, value) {
  const list = Array.isArray(arr) ? [...arr] : [];
  const idx = list.findIndex((e) => e.year === year);
  if (idx >= 0) {
    list[idx] = { ...list[idx], [key]: value };
  } else {
    list.push({ year, [key]: value });
  }
  return list;
}

const AVATAR_GRADIENTS = [
  "bg-[#1E293B]",
  "bg-gradient-to-br from-blue-900 to-blue-500",
  "bg-gradient-to-br from-amber-800 to-amber-500",
  "bg-gradient-to-br from-rose-700 to-rose-500",
  "bg-gradient-to-br from-green-800 to-green-500",
  "bg-gradient-to-br from-violet-800 to-violet-400",
];
function avatarFor(u) {
  const seed = String(u?.id || "").charCodeAt(0) || 0;
  return AVATAR_GRADIENTS[seed % AVATAR_GRADIENTS.length];
}
function initialsFor(u) {
  if (!u) return "?";
  const f = (u.firstname || "").trim();
  const l = (u.lastname || "").trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  return (f || l || "?").slice(0, 2).toUpperCase();
}

const APPT_STATUSES = [
  { key: "pending", label: "Pending" },
  { key: "won", label: "Won" },
  { key: "closed", label: "Closed" },
  { key: "lost", label: "Lost" },
  { key: "deferred", label: "Deferred" },
  { key: "next-year", label: "Next Year" },
];

function statusBadgeClass(status) {
  switch (status) {
    case "won":
      return "bg-green-100 text-green-800";
    case "closed":
      return "bg-emerald-200 text-emerald-900";
    case "lost":
      return "bg-red-100 text-red-800";
    case "deferred":
      return "bg-slate-200 text-slate-700";
    case "next-year":
      return "bg-blue-100 text-blue-800";
    case "pending":
    default:
      return "bg-amber-100 text-amber-800";
  }
}

function Goals() {
  const [{ user, users, listingAppointments }, dispatch] = useStateValue();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [filterYear, setFilterYear] = useState(String(currentYear));
  const [yearDropdownVisible, setYearDropdownVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // null = closed
  const [saving, setSaving] = useState(false);
  const [openHouses, setOpenHouses] = useState([]);

  // Live-subscribe to open houses so each agent's hosted-count stays current.
  useEffect(() => {
    const unsub = subscribeOpenHouses((rows) => setOpenHouses(rows || []));
    return () => unsub && unsub();
  }, []);

  // Count of open houses hosted per agent, for the selected year.
  // Matches by hostingAgentId (new) with hostingAgentEmail fallback for
  // older OHs that pre-date the assignment field.
  const ohCountByAgent = useMemo(() => {
    const map = {};
    (openHouses || []).forEach((oh) => {
      const ohYear = oh.date ? Number(String(oh.date).slice(0, 4)) : null;
      if (ohYear !== selectedYear) return;
      const ownerId =
        oh.hostingAgentId ||
        (users || []).find(
          (u) =>
            u.email &&
            oh.hostingAgentEmail &&
            u.email.toLowerCase() === oh.hostingAgentEmail.toLowerCase(),
        )?.id;
      if (!ownerId) return;
      map[ownerId] = (map[ownerId] || 0) + 1;
    });
    return map;
  }, [openHouses, selectedYear, users]);

  // Listing appointment modal state. `editingAppt` of "new" opens a blank form,
  // an object opens edit-mode for that appt, null is closed.
  const [editingAppt, setEditingAppt] = useState(null);
  const [savingAppt, setSavingAppt] = useState(false);

  const isAdmin = user?.role === "Admin";
  const isEmployee = user?.role === "Employee";

  // Producers eligible to have goals: Admins (Team Leads) and Employees (Agents) only.
  // Excludes deleted (type 1), disabled (type 2), supervisors, and accountants.
  const producers = useMemo(
    () =>
      (users || []).filter(
        (u) =>
          u?.type !== 1 &&
          u?.type !== 2 &&
          (u?.role === "Admin" || u?.role === "Employee"),
      ),
    [users],
  );

  // Team totals for the selected year.
  const teamTotals = useMemo(() => {
    return producers.reduce(
      (acc, u) => {
        const g = getGoalsFor(u, selectedYear);
        acc.gci += Number(g.gci || 0);
        acc.volume += Number(g.volume || 0);
        acc.units += Number(g.units || 0);
        if (Number(g.gci) || Number(g.volume) || Number(g.units)) {
          acc.withGoals += 1;
        }
        return acc;
      },
      { gci: 0, volume: 0, units: 0, withGoals: 0 },
    );
  }, [producers, selectedYear]);

  async function saveGoals(targetUserId, year, vals) {
    const target = users.find((u) => u.id === targetUserId);
    if (!target) return;
    try {
      setSaving(true);
      const newYearlyGoals = upsertEntry(
        target.yearlyGoals,
        year,
        "amount",
        vals.gci === "" ? "" : Number(vals.gci),
      );
      const newUnitsGoals = upsertEntry(
        target.closedyearlygoals,
        year,
        "count",
        vals.units === "" ? "" : Number(vals.units),
      );
      const newVolumeGoals = upsertEntry(
        target.closedvolumeyearlygoals,
        year,
        "amount",
        vals.volume === "" ? "" : Number(vals.volume),
      );
      const newOpenHouseGoals = upsertEntry(
        target.openhouseyearlygoals,
        year,
        "count",
        vals.openHouses === "" ? "" : Number(vals.openHouses),
      );

      await setDoc(
        doc(firestored, "users", targetUserId),
        {
          yearlyGoals: newYearlyGoals,
          closedyearlygoals: newUnitsGoals,
          closedvolumeyearlygoals: newVolumeGoals,
          openhouseyearlygoals: newOpenHouseGoals,
        },
        { merge: true },
      );

      const refreshed = await getAllusers("users");
      dispatch({ type: actionType.STORE_USERS, payload: refreshed });
      toast.success("Goals saved");
      setEditingUser(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save goals");
    } finally {
      setSaving(false);
    }
  }

  // ───── LISTING APPOINTMENTS ─────
  async function refreshAppts() {
    try {
      const items = await getListingAppointments();
      dispatch({
        type: actionType.STORE_LISTING_APPOINTMENTS,
        payload: items,
      });
    } catch (err) {
      console.error("Failed to refresh listing appointments:", err);
    }
  }

  // Keep the Listing Pipeline board in step with the appointment's status:
  //   Deferred / Next Year → the lead card sits in that pipeline column
  //   Won / Closed         → card leaves the board (converted)
  //   Lost                 → card marked lost (hidden by default)
  async function syncPipelineCard(appt) {
    const status = String(appt.status || "pending").toLowerCase();
    const stageFor = { deferred: "Deferred", "next-year": "Next Year" }[status];
    // The edit form may not carry leadId — recover it from the loaded list.
    const existing = (listingAppointments || []).find((a) => a.id === appt.id);
    appt = { ...appt, leadId: appt.leadId || existing?.leadId || "" };
    try {
      if (stageFor) {
        if (appt.leadId) {
          await updateListingLead(appt.leadId, {
            stage: stageFor,
            status: "active",
          });
        } else {
          const agent = (users || []).find((u) => u.id === appt.agentId);
          const res = await saveListingLead({
            contactName: appt.prospectName || "",
            propertyAddress: appt.address || "",
            stage: stageFor,
            appointmentAt: appt.appointmentDate || "",
            notes: appt.notes || "",
            assignedAgent: appt.agentId || "",
            assignedAgentName: agent
              ? [agent.firstname, agent.lastname].filter(Boolean).join(" ")
              : "",
            createdby: user?.id || "",
            sourceAppointmentId: appt.id || "",
          });
          if (res?.success && appt.id) {
            await updateListingAppointment(appt.id, { leadId: res.id });
          }
        }
      } else if (appt.leadId && (status === "won" || status === "closed")) {
        await updateListingLead(appt.leadId, { status: "converted" });
      } else if (appt.leadId && status === "lost") {
        await updateListingLead(appt.leadId, { status: "lost" });
      }
    } catch (e) {
      // Sync is best-effort — the appointment itself already saved.
      console.error("Pipeline sync failed:", e);
    }
  }

  async function saveAppt(vals) {
    try {
      setSavingAppt(true);
      const isNew = !vals.id;
      const apptYear = vals.appointmentDate
        ? new Date(vals.appointmentDate).getFullYear()
        : selectedYear;
      const payload = {
        ...vals,
        forYear: apptYear,
        agentId: vals.agentId || user?.id,
      };
      let savedId = vals.id;
      if (isNew) {
        savedId = await saveListingAppointment(payload);
      } else {
        await updateListingAppointment(vals.id, payload);
      }
      await syncPipelineCard({ ...payload, id: savedId });
      await refreshAppts();
      toast.success(isNew ? "Appointment logged" : "Appointment updated");
      setEditingAppt(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save appointment");
    } finally {
      setSavingAppt(false);
    }
  }

  async function removeAppt(id) {
    if (!id) return;
    if (!window.confirm("Delete this appointment? This cannot be undone."))
      return;
    try {
      await deleteListingAppointment(id);
      await refreshAppts();
      toast.success("Appointment deleted");
      setEditingAppt(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete");
    }
  }

  // Appointments visible to the current user, scoped to the selected year.
  const visibleAppts = useMemo(() => {
    const all = listingAppointments || [];
    const inYear = all.filter((a) => Number(a.forYear) === selectedYear);
    if (isAdmin) return inYear;
    // Non-admins only see their own.
    return inYear.filter((a) => a.agentId === user?.id);
  }, [listingAppointments, selectedYear, isAdmin, user?.id]);

  // Stat counts for the visible appointments.
  const apptStats = useMemo(() => {
    const counts = {
      total: visibleAppts.length,
      pending: 0,
      won: 0,
      closed: 0,
      lost: 0,
      deferred: 0,
      "next-year": 0,
    };
    visibleAppts.forEach((a) => {
      counts[a.status || "pending"] =
        (counts[a.status || "pending"] || 0) + 1;
    });
    return counts;
  }, [visibleAppts]);

  // Employee can only edit themselves. Admin can edit anyone. Everyone else view-only.
  const canEdit = (targetUserId) => {
    if (isAdmin) return true;
    if (isEmployee) return targetUserId === user.id;
    return false;
  };

  // For the Employee single-card layout.
  const employeeGoals = isEmployee ? getGoalsFor(user, selectedYear) : null;

  return (
    <div className="bg-slate-100 min-h-screen">
      <PageHeader
        title="Goals & Targets"
        subtitle={
          isEmployee
            ? "Set your goals for the year"
            : isAdmin
              ? "Manage goals for every team member"
              : "View team goals for the year"
        }
        actions={
          <div className="relative">
            <button
              onClick={() => setYearDropdownVisible(!yearDropdownVisible)}
              className="bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 px-3 py-1.5 rounded-md text-[13px] font-medium cursor-pointer"
            >
              {filterYear}
            </button>
            <YearFilterDropdown
              visible={yearDropdownVisible}
              onSelect={(year) => {
                setFilterYear(year);
                setSelectedYear(Number(year));
                setYearDropdownVisible(false);
              }}
              activateOutsideClick={() => setYearDropdownVisible(false)}
              style={{ top: "40px", right: "0px" }}
            />
          </div>
        }
      />

      <div className="max-w-[1380px] mx-auto px-9 pt-7 pb-20">
        {/* ============ TEAM TOTALS (for non-employee views) ============ */}
        {!isEmployee && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-5">
            <SummaryCard
              label="Team GCI Goal"
              value={`$${formatNumberShort(teamTotals.gci)}`}
              sub={`${selectedYear}`}
            />
            <SummaryCard
              label="Team Volume Goal"
              value={`$${formatNumberShort(teamTotals.volume)}`}
              sub={`${selectedYear}`}
            />
            <SummaryCard
              label="Team Units Goal"
              value={`${teamTotals.units}`}
              sub={`${selectedYear}`}
            />
            <SummaryCard
              label="Agents With Goals"
              value={`${teamTotals.withGoals} / ${producers.length}`}
              sub="for selected year"
            />
          </div>
        )}

        {/* ============ LISTING APPOINTMENTS ============ */}
        <ListingApptsSection
          appts={visibleAppts}
          stats={apptStats}
          producers={producers}
          isAdmin={isAdmin}
          currentUserId={user?.id}
          selectedYear={selectedYear}
          onAdd={() => setEditingAppt("new")}
          onRowClick={(appt) => setEditingAppt(appt)}
        />

        {/* ============ EMPLOYEE: single-card form ============ */}
        {isEmployee && (
          <SectionPanel
            title="My Annual Goals"
            subtitle={`Set what you're aiming for in ${selectedYear}`}
          >
            <GoalForm
              initial={employeeGoals}
              year={selectedYear}
              saving={saving}
              onSave={(vals) => saveGoals(user.id, selectedYear, vals)}
            />
            <OpenHouseProgress
              hosted={ohCountByAgent[user?.id] || 0}
              goal={Number(employeeGoals?.openHouses) || 0}
              year={selectedYear}
            />
          </SectionPanel>
        )}

        {/* ============ ADMIN / SUPERVISOR / ACCOUNTANT: agent grid ============ */}
        {!isEmployee && (
          <SectionPanel
            title="Annual Goals by Agent"
            subtitle={
              isAdmin
                ? "Click any agent to set or update their goals"
                : "Read-only view of team goals"
            }
          >
            {producers.length === 0 ? (
              <div className="py-8 text-center text-gray-400 text-[13px]">
                No team members found
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {producers
                  .slice()
                  .sort((a, b) => {
                    const an = `${a.firstname || ""} ${a.lastname || ""}`
                      .trim()
                      .toLowerCase();
                    const bn = `${b.firstname || ""} ${b.lastname || ""}`
                      .trim()
                      .toLowerCase();
                    return an.localeCompare(bn);
                  })
                  .map((u) => (
                    <AgentGoalCard
                      key={u.id}
                      agent={u}
                      goals={getGoalsFor(u, selectedYear)}
                      openHousesHosted={ohCountByAgent[u.id] || 0}
                      canEdit={canEdit(u.id)}
                      onClick={() => setEditingUser(u)}
                    />
                  ))}
              </div>
            )}
          </SectionPanel>
        )}

        {/* ============ FUTURE: room for more target sections ============ */}
        <SectionPanel
          title="Additional Targets"
          subtitle="Coming soon: cycle-time and conversion-rate targets"
          muted
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <PlaceholderTarget label="Cycle Time Targets" />
            <PlaceholderTarget label="Conversion Targets" />
          </div>
        </SectionPanel>
      </div>

      {/* ============ EDIT MODAL ============ */}
      {editingUser && (
        <EditGoalModal
          agent={editingUser}
          year={selectedYear}
          readOnly={!canEdit(editingUser.id)}
          saving={saving}
          onClose={() => setEditingUser(null)}
          onSave={(vals) => saveGoals(editingUser.id, selectedYear, vals)}
        />
      )}

      {/* ============ APPOINTMENT MODAL ============ */}
      {editingAppt && (
        <AppointmentModal
          appt={editingAppt === "new" ? null : editingAppt}
          isAdmin={isAdmin}
          producers={producers}
          defaultAgentId={user?.id}
          saving={savingAppt}
          canDelete={
            editingAppt !== "new" &&
            (isAdmin || editingAppt.agentId === user?.id)
          }
          readOnly={
            editingAppt !== "new" &&
            !isAdmin &&
            editingAppt.agentId !== user?.id
          }
          onClose={() => setEditingAppt(null)}
          onSave={saveAppt}
          onDelete={() =>
            editingAppt !== "new" && removeAppt(editingAppt.id)
          }
        />
      )}
    </div>
  );
}

// ============ SUB-COMPONENTS ============

function SectionPanel({ title, subtitle, muted, children }) {
  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-black/[0.04] p-6 mb-5 ${
        muted ? "opacity-90" : ""
      }`}
    >
      <div className="flex items-center justify-between border-b border-black/[0.08] pb-3.5 mb-4">
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 bg-[#1E293B] rounded-md inline-flex items-center justify-center flex-shrink-0">
            <Target size={14} strokeWidth={2} className="text-white" />
          </span>
          <div>
            <div className="text-[15px] font-semibold text-slate-900">
              {title}
            </div>
            {subtitle && (
              <div className="text-[11px] text-gray-500 mt-0.5">{subtitle}</div>
            )}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}

function SummaryCard({ label, value, sub }) {
  return (
    <div className="p-[14px_16px] bg-white rounded-lg border border-black/[0.04] shadow-sm">
      <div className="text-xs text-gray-500 font-medium">{label}</div>
      <div className="text-[20px] font-bold text-slate-900 mt-1 -tracking-[0.01em]">
        {value}
      </div>
      {sub && <div className="text-[11.5px] text-gray-500 mt-1.5">{sub}</div>}
    </div>
  );
}

function AgentGoalCard({ agent, goals, openHousesHosted = 0, canEdit, onClick }) {
  const initials = initialsFor(agent);
  const avatar = avatarFor(agent);
  const isSet =
    goals.gci || goals.volume || goals.units || goals.openHouses;
  const ohGoal = Number(goals.openHouses) || 0;
  const ohValue = ohGoal
    ? `${openHousesHosted} / ${ohGoal}`
    : openHousesHosted;
  return (
    <div
      onClick={onClick}
      className="p-5 bg-gray-50 hover:bg-slate-100 hover:border-black/[0.08] border border-black/[0.03] rounded-lg cursor-pointer transition group"
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className={`w-10 h-10 rounded-full ${avatar} text-white flex items-center justify-center font-semibold text-[13px]`}
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">
            {agent.firstname} {agent.lastname}
          </div>
          <div className="text-[11.5px] text-gray-500 truncate">
            {agent.role === "Admin"
              ? "Team Lead"
              : agent.role === "Employee"
                ? "Agent"
                : agent.role}
          </div>
        </div>
        <ChevronRight
          size={16}
          className="text-slate-300 group-hover:text-[#1E293B] group-hover:translate-x-0.5 transition"
        />
      </div>

      {isSet ? (
        <div className="grid grid-cols-4 gap-2">
          <GoalStat
            label="GCI"
            value={goals.gci ? `$${formatNumberShort(Number(goals.gci))}` : "—"}
          />
          <GoalStat
            label="Volume"
            value={
              goals.volume
                ? `$${formatNumberShort(Number(goals.volume))}`
                : "—"
            }
          />
          <GoalStat label="Units" value={goals.units || "—"} />
          <GoalStat label="Open Houses" value={ohValue} />
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-[12.5px] text-amber-700 font-medium">
            <Plus size={14} />
            {canEdit ? "Set goals for this year" : "No goals set"}
          </div>
          {openHousesHosted > 0 && (
            <div className="text-[11px] text-slate-500">
              {openHousesHosted} open house
              {openHousesHosted === 1 ? "" : "s"} hosted this year
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function OpenHouseProgress({ hosted, goal, year }) {
  const hasGoal = goal > 0;
  const pct = hasGoal ? Math.min(100, Math.round((hosted / goal) * 100)) : 0;
  const reached = hasGoal && hosted >= goal;
  return (
    <div className="mt-5 p-4 bg-slate-50 rounded-lg border border-slate-200">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[11px] text-gray-500 font-semibold uppercase tracking-wider">
            Open Houses · {year}
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-0.5">
            {hosted}
            {hasGoal && (
              <span className="text-base text-gray-400 font-medium ml-1">
                / {goal}
              </span>
            )}
          </div>
        </div>
        {hasGoal && (
          <div
            className={`text-xs font-semibold ${reached ? "text-emerald-600" : "text-slate-500"}`}
          >
            {reached ? "Goal reached" : `${pct}% of goal`}
          </div>
        )}
      </div>
      {hasGoal ? (
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              reached
                ? "bg-gradient-to-r from-emerald-500 to-emerald-400"
                : "bg-gradient-to-r from-indigo-500 to-indigo-400"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : (
        <div className="text-[12px] text-slate-500">
          Set an Open House goal above to track your progress.
        </div>
      )}
    </div>
  );
}

function GoalStat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
        {label}
      </div>
      <div className="text-[13.5px] font-semibold text-slate-900 mt-0.5">
        {value}
      </div>
    </div>
  );
}

function PlaceholderTarget({ label }) {
  return (
    <div className="p-[14px_16px] bg-gray-50 rounded-lg border border-dashed border-gray-300">
      <div className="text-xs text-gray-500 font-medium">{label}</div>
      <div className="text-[13px] font-semibold text-gray-400 mt-1">
        Coming soon
      </div>
    </div>
  );
}

// Inline editable form (used by Employee view).
function GoalForm({ initial, year, saving, onSave }) {
  const [gci, setGci] = useState(initial?.gci ?? "");
  const [volume, setVolume] = useState(initial?.volume ?? "");
  const [units, setUnits] = useState(initial?.units ?? "");
  const [openHouses, setOpenHouses] = useState(initial?.openHouses ?? "");

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <Field
          label={`GCI Goal · ${year}`}
          prefix="$"
          value={gci}
          onChange={setGci}
          placeholder="e.g. 250000"
        />
        <Field
          label={`Volume Goal · ${year}`}
          prefix="$"
          value={volume}
          onChange={setVolume}
          placeholder="e.g. 25000000"
        />
        <Field
          label={`Units Goal · ${year}`}
          value={units}
          onChange={setUnits}
          placeholder="e.g. 40"
        />
        <Field
          label={`Open House Goal · ${year}`}
          value={openHouses}
          onChange={setOpenHouses}
          placeholder="e.g. 24"
        />
      </div>
      <button
        onClick={() => onSave({ gci, volume, units, openHouses })}
        disabled={saving}
        className="inline-flex items-center gap-2 bg-[#1E293B] hover:bg-slate-700 disabled:opacity-60 text-white px-4 py-2 rounded-md text-sm font-medium"
      >
        <Save size={14} />
        {saving ? "Saving…" : "Save Goals"}
      </button>
    </div>
  );
}

function Field({ label, prefix, value, onChange, placeholder, readOnly }) {
  return (
    <div>
      <label className="block text-[11px] text-gray-500 font-semibold mb-1.5 uppercase tracking-wider">
        {label}
      </label>
      <div className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 focus-within:border-[#1E293B]">
        {prefix && (
          <span className="text-gray-400 font-medium mr-1">{prefix}</span>
        )}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          readOnly={readOnly}
          className="flex-1 outline-none text-sm bg-transparent"
        />
      </div>
    </div>
  );
}

function EditGoalModal({ agent, year, readOnly, saving, onClose, onSave }) {
  const [gci, setGci] = useState(getGoalsFor(agent, year).gci);
  const [volume, setVolume] = useState(getGoalsFor(agent, year).volume);
  const [units, setUnits] = useState(getGoalsFor(agent, year).units);
  const [openHouses, setOpenHouses] = useState(
    getGoalsFor(agent, year).openHouses,
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-full ${avatarFor(agent)} text-white flex items-center justify-center font-semibold text-[13px]`}
            >
              {initialsFor(agent)}
            </div>
            <div>
              <div className="text-base font-semibold text-slate-900">
                {agent.firstname} {agent.lastname}
              </div>
              <div className="text-[11.5px] text-gray-500">
                {readOnly ? "View goals" : "Edit goals"} · {year}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-slate-900 p-1"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5">
          <div className="space-y-3">
            <Field
              label={`GCI Goal · ${year}`}
              prefix="$"
              value={gci}
              onChange={setGci}
              placeholder="e.g. 250000"
              readOnly={readOnly}
            />
            <Field
              label={`Volume Goal · ${year}`}
              prefix="$"
              value={volume}
              onChange={setVolume}
              placeholder="e.g. 25000000"
              readOnly={readOnly}
            />
            <Field
              label={`Units Goal · ${year}`}
              value={units}
              onChange={setUnits}
              placeholder="e.g. 40"
              readOnly={readOnly}
            />
            <Field
              label={`Open House Goal · ${year}`}
              value={openHouses}
              onChange={setOpenHouses}
              placeholder="e.g. 24"
              readOnly={readOnly}
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900"
          >
            {readOnly ? "Close" : "Cancel"}
          </button>
          {!readOnly && (
            <button
              onClick={() => onSave({ gci, volume, units, openHouses })}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-[#1E293B] hover:bg-slate-700 disabled:opacity-60 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              <Save size={14} />
              {saving ? "Saving…" : "Save"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ LISTING APPOINTMENTS UI ============

function ListingApptsSection({
  appts,
  stats,
  producers,
  isAdmin,
  currentUserId,
  selectedYear,
  onAdd,
  onRowClick,
}) {
  const agentName = (id) => {
    const a = producers.find((u) => u.id === id);
    return a ? `${a.firstname} ${a.lastname}` : "—";
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-black/[0.04] p-6 mb-5">
      <div className="flex items-center justify-between border-b border-black/[0.08] pb-3.5 mb-4">
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 bg-[#1E293B] rounded-md inline-flex items-center justify-center flex-shrink-0">
            <ClipboardList size={14} strokeWidth={2} className="text-white" />
          </span>
          <div>
            <div className="text-[15px] font-semibold text-slate-900">
              Listing Appointments
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              {isAdmin
                ? `Team-wide tracking for ${selectedYear}`
                : `Your appointments for ${selectedYear}`}
            </div>
          </div>
        </div>
        <button
          onClick={onAdd}
          className="inline-flex items-center gap-1.5 bg-[#1E293B] hover:bg-slate-700 text-white px-3 py-1.5 rounded-md text-[13px] font-medium"
        >
          <Plus size={14} />
          Log Appointment
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
        <ApptStatCard label="Total Appts" value={stats.total} />
        <ApptStatCard label="Pending" value={stats.pending} tone="amber" />
        <ApptStatCard label="Won" value={stats.won} tone="green" />
        <ApptStatCard label="Closed" value={stats.closed} tone="emerald" />
        <ApptStatCard label="Lost" value={stats.lost} tone="red" />
        <ApptStatCard label="Deferred" value={stats.deferred} tone="slate" />
        <ApptStatCard
          label="Next Year"
          value={stats["next-year"]}
          tone="blue"
        />
      </div>

      {appts.length === 0 ? (
        <div className="py-10 text-center text-gray-400 text-[13px]">
          <ClipboardList
            size={28}
            className="mx-auto text-gray-300 mb-2"
            strokeWidth={1.8}
          />
          No listing appointments logged for {selectedYear}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-3 py-2.5 text-left text-[10.5px] font-semibold text-gray-500 uppercase tracking-wider pl-1">
                  Date
                </th>
                <th className="px-3 py-2.5 text-left text-[10.5px] font-semibold text-gray-500 uppercase tracking-wider">
                  Prospect
                </th>
                <th className="px-3 py-2.5 text-left text-[10.5px] font-semibold text-gray-500 uppercase tracking-wider">
                  Address
                </th>
                {isAdmin && (
                  <th className="px-3 py-2.5 text-left text-[10.5px] font-semibold text-gray-500 uppercase tracking-wider">
                    Agent
                  </th>
                )}
                <th className="px-3 py-2.5 text-left text-[10.5px] font-semibold text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {appts
                .slice()
                .sort(
                  (a, b) =>
                    new Date(b.appointmentDate || 0) -
                    new Date(a.appointmentDate || 0),
                )
                .map((a) => (
                  <tr
                    key={a.id}
                    onClick={() => onRowClick(a)}
                    className="border-b border-slate-100 last:border-b-0 hover:bg-gray-50 cursor-pointer transition"
                  >
                    <td className="px-3 py-3 pl-1 text-slate-900 font-medium whitespace-nowrap">
                      {a.appointmentDate
                        ? new Date(a.appointmentDate).toLocaleDateString(
                            undefined,
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )
                        : "—"}
                    </td>
                    <td className="px-3 py-3 text-slate-900 font-medium">
                      {a.prospectName || "—"}
                    </td>
                    <td className="px-3 py-3 text-gray-500">
                      {a.address || (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-3 text-gray-500">
                        {agentName(a.agentId)}
                      </td>
                    )}
                    <td className="px-3 py-3">
                      <span
                        className={`inline-block text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${statusBadgeClass(
                          a.status || "pending",
                        )}`}
                      >
                        {APPT_STATUSES.find(
                          (s) => s.key === (a.status || "pending"),
                        )?.label || a.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <ChevronRight size={14} className="text-slate-300" />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ApptStatCard({ label, value, tone }) {
  const toneClass =
    tone === "green"
      ? "text-green-700"
      : tone === "emerald"
        ? "text-emerald-800"
        : tone === "red"
          ? "text-red-700"
          : tone === "amber"
            ? "text-amber-700"
            : tone === "blue"
              ? "text-blue-700"
              : tone === "slate"
                ? "text-slate-700"
                : "text-slate-900";
  return (
    <div className="p-[14px_16px] bg-gray-50 rounded-lg border border-black/[0.03]">
      <div className="text-xs text-gray-500 font-medium">{label}</div>
      <div className={`text-[22px] font-bold mt-1 -tracking-[0.01em] ${toneClass}`}>
        {value || 0}
      </div>
    </div>
  );
}

function AppointmentModal({
  appt,
  isAdmin,
  producers,
  defaultAgentId,
  saving,
  canDelete,
  readOnly,
  onClose,
  onSave,
  onDelete,
}) {
  const isNew = !appt;
  const todayStr = new Date().toISOString().slice(0, 10);
  const [appointmentDate, setAppointmentDate] = useState(
    appt?.appointmentDate || todayStr,
  );
  const [prospectName, setProspectName] = useState(appt?.prospectName || "");
  const [address, setAddress] = useState(appt?.address || "");
  const [agentId, setAgentId] = useState(
    appt?.agentId || defaultAgentId || "",
  );
  const [status, setStatus] = useState(appt?.status || "pending");
  const [notes, setNotes] = useState(appt?.notes || "");

  const handleSave = () => {
    if (!appointmentDate) {
      toast.error("Appointment date is required");
      return;
    }
    if (!prospectName.trim()) {
      toast.error("Prospect name is required");
      return;
    }
    onSave({
      ...(appt ? { id: appt.id } : {}),
      appointmentDate,
      prospectName: prospectName.trim(),
      address: address.trim(),
      agentId,
      status,
      notes: notes.trim(),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-md bg-[#1E293B] inline-flex items-center justify-center">
              <ClipboardList size={16} className="text-white" />
            </span>
            <div>
              <div className="text-base font-semibold text-slate-900">
                {isNew
                  ? "Log Listing Appointment"
                  : readOnly
                    ? "View Appointment"
                    : "Edit Appointment"}
              </div>
              <div className="text-[11.5px] text-gray-500">
                {isNew ? "Add a new entry" : `ID: ${appt?.id?.slice(0, 8)}…`}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-slate-900 p-1"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-3">
          <div>
            <label className="block text-[11px] text-gray-500 font-semibold mb-1.5 uppercase tracking-wider">
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={12} /> Appointment Date
              </span>
            </label>
            <input
              type="date"
              value={appointmentDate}
              onChange={(e) => setAppointmentDate(e.target.value)}
              readOnly={readOnly}
              className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#1E293B]"
            />
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 font-semibold mb-1.5 uppercase tracking-wider">
              Prospect Name
            </label>
            <input
              type="text"
              value={prospectName}
              onChange={(e) => setProspectName(e.target.value)}
              readOnly={readOnly}
              placeholder="e.g. Sarah Anderson"
              className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#1E293B]"
            />
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 font-semibold mb-1.5 uppercase tracking-wider">
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={12} /> Property Address (optional)
              </span>
            </label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              readOnly={readOnly}
              placeholder="e.g. 2847 Lake Harriet Pkwy"
              className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#1E293B]"
            />
          </div>

          {isAdmin && (
            <div>
              <label className="block text-[11px] text-gray-500 font-semibold mb-1.5 uppercase tracking-wider">
                Agent
              </label>
              <select
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                disabled={readOnly}
                className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#1E293B]"
              >
                <option value="">— Select agent —</option>
                {producers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.firstname} {p.lastname}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-[11px] text-gray-500 font-semibold mb-1.5 uppercase tracking-wider">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={readOnly}
              className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#1E293B]"
            >
              {APPT_STATUSES.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 font-semibold mb-1.5 uppercase tracking-wider">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              readOnly={readOnly}
              rows={3}
              placeholder="Anything worth remembering about this appointment"
              className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#1E293B] resize-y"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div>
            {canDelete && !readOnly && (
              <button
                onClick={onDelete}
                className="inline-flex items-center gap-1.5 text-red-700 hover:text-red-900 text-[13px] font-medium"
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900"
            >
              {readOnly ? "Close" : "Cancel"}
            </button>
            {!readOnly && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-[#1E293B] hover:bg-slate-700 disabled:opacity-60 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                <Save size={14} />
                {saving ? "Saving…" : isNew ? "Log Appointment" : "Save"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Goals;
